import { useState } from 'react'
import { Text, Stack, Tabs } from '@mantine/core'
import { useAuthStore, useCan } from '@/store/auth'
import { useDashboardData } from '@/hooks/useDashboardData'
import { Callout } from '@/components/ui'
import { DashboardHeader } from '@/components/DashboardHeader'
import { BusinessTab } from './BusinessTab'
import { InfrastructureTab } from './InfrastructureTab'
import { WhatsApiTenantTab } from './WhatsApiTenantTab'

/**
 * PO Dashboard Component (TASK-1223, TASK-1224, TASK-1226)
 */
export function PODashboard() {
  const { user } = useAuthStore()
  const can = useCan()
  const [activeTab, setActiveTab] = useState<string | null>('business')

  const { tenant, tenantId, preferences } = useDashboardData('po')

  const tid = tenantId ?? null
  const canViewBusiness = can('chamados', 'read', tid) || can('finops', 'read', tid)
  const canViewInfra = can('recursos', 'read', tid)
  const canObserveWhatsApi = tenantId != null && (
    can('whatsapi', 'observe', tenantId) || can('whatsapi', 'read', tenantId)
  )

  const hiddenCards = preferences.data?.hidden_cards ?? []
  const showBusiness = !hiddenCards.includes('business') && canViewBusiness
  const showInfra = !hiddenCards.includes('infrastructure') && canViewInfra
  const showWhatsApi = canObserveWhatsApi

  const visibleTabs: string[] = []
  if (showBusiness) visibleTabs.push('business')
  if (showInfra) visibleTabs.push('infrastructure')
  if (showWhatsApi) visibleTabs.push('whatsapi')

  // If no tabs are visible, show warning
  if (visibleTabs.length === 0) {
    return (
      <Stack gap="xl">
        <DashboardHeader title="Dashboard PO" />
        <Callout variant="warning" title="Sem permissões">
          <Text size="sm">
            Você não possui permissões para visualizar nenhuma aba do dashboard.
          </Text>
        </Callout>
      </Stack>
    )
  }

  const initialTab = activeTab && visibleTabs.includes(activeTab) ? activeTab : visibleTabs[0]

  return (
    <Stack gap="xl">
      <DashboardHeader title="Dashboard PO" />

      {tenant && (
        <Text size="sm" c="dimmed">
          Tenant: {tenant.name} [{tenant.environment.toUpperCase()}]
        </Text>
      )}

      <Tabs value={initialTab} onChange={setActiveTab}>
        <Tabs.List>
          {showBusiness && <Tabs.Tab value="business">Negócio</Tabs.Tab>}
          {showInfra && <Tabs.Tab value="infrastructure">Infraestrutura</Tabs.Tab>}
          {showWhatsApi && <Tabs.Tab value="whatsapi">WhatsApi</Tabs.Tab>}
        </Tabs.List>

        {showBusiness && (
          <Tabs.Panel value="business" pt="xl">
            <BusinessTab />
          </Tabs.Panel>
        )}

        {showInfra && (
          <Tabs.Panel value="infrastructure" pt="xl">
            <InfrastructureTab />
          </Tabs.Panel>
        )}

        {showWhatsApi && (
          <Tabs.Panel value="whatsapi" pt="xl">
            <WhatsApiTenantTab tenantId={tenantId as number} />
          </Tabs.Panel>
        )}
      </Tabs>

      {!tenant && (
        <Callout variant="warning" title="Sem tenant associado">
          <Text size="sm">
            Peça ao administrador para associar um tenant ao seu usuário em{' '}
            <strong>Administração — Usuários</strong>.
          </Text>
        </Callout>
      )}
    </Stack>
  )
}
